import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

const CHATGPT_GPT =
  "https://chatgpt.com/g/g-69fe65ea0e3481918132c4be39a6239c-comos-commerce";
const CLAUDE_MCP = "https://mcp.comos-federation.com/mcp";

/**
 * DemoPage — the live proof. (CO 069)
 *
 * "Ask ChatGPT or Claude to shop our network" is a real working flow, not a
 * slide (corpus: both live since 2026-05-12). The most credible artifact on the
 * site — a skeptic can try it right now.
 */
export function DemoPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              Live, right now
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              Ask ChatGPT or Claude to shop our network.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Not a mockup. Not "coming soon." A real agent, talking to a real
              merchant network, completing a real checkout — today. Pick your
              assistant and try it.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <motion.div
            {...inView}
            variants={stagger}
            className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto"
          >
            <motion.div variants={fadeUp}>
              <Card hover className="h-full flex flex-col">
                <h3 className="text-display-sm mb-2">ChatGPT</h3>
                <p className="text-mute dark:text-mute-soft text-sm flex-1">
                  Open the ComOS Commerce custom GPT and ask it to find a
                  product and check out across our merchant network.
                </p>
                <a
                  href={CHATGPT_GPT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonStyles({
                    variant: "primary",
                    className: "mt-5 w-full",
                  })}
                >
                  Open in ChatGPT →
                </a>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card hover className="h-full flex flex-col">
                <h3 className="text-display-sm mb-2">Claude</h3>
                <p className="text-mute dark:text-mute-soft text-sm flex-1">
                  Add the ComOS MCP connector in Claude, then ask Claude to shop
                  the network. OAuth 2.1 + PKCE, production-grade.
                </p>
                <div className="mt-5">
                  <code className="block rounded-lg bg-ink text-paper dark:bg-black font-mono text-xs p-3 overflow-x-auto">
                    {CLAUDE_MCP}
                  </code>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHead
            align="center"
            eyebrow="What's actually happening"
            title="One connection, the whole commerce control plane."
            lead="Search the catalog, add to cart, select shipping, start a Stripe-backed checkout — every step is a real call to the ComOS Federation Gateway, the same surface any agent you build would use."
          />
          <motion.div
            {...inView}
            variants={fadeUp}
            className="mt-10 max-w-2xl mx-auto"
          >
            <Card className="bg-ink text-paper dark:bg-black font-mono text-sm overflow-x-auto">
              <p className="text-mute-soft mb-3">
                # The flow the assistant runs
              </p>
              <p>
                <span className="text-signal-bright">catalog.search</span> →
                find the product
              </p>
              <p>
                <span className="text-signal-bright">shipping_get_rates</span>{" "}
                → pick a rate
              </p>
              <p>
                <span className="text-signal-bright">order_create</span> →
                reserve, price, and settle in one atomic call
              </p>
            </Card>
          </motion.div>
        </Container>
      </Section>

      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              The moment you join, you're in that network.
            </h2>
            <p className="text-lead text-paper/70 mt-6">
              Every agent that can reach the gateway can find and buy from you —
              free, day one. This demo is what your store joins.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/start"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Start building — free
              </Link>
              <Link
                to="/agents"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                Build on the endpoint
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
